import React, { useEffect, useState, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import PerfectScrollbar from 'react-perfect-scrollbar';
import { makeStyles } from '@material-ui/styles';
import {
  Card,
  CardActions,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TablePagination,
  Button,
  CircularProgress,
  List,
  ListItem,
  Tooltip,
  Typography,
  Grid,
  Avatar,
  Box,
  IconButton
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import FileCopyOutlinedIcon from '@material-ui/icons/FileCopyOutlined';
import MouseIcon from '@material-ui/icons/Mouse';
import LinkIcon from '@material-ui/icons/Link';
import Skeleton from 'react-loading-skeleton';
import { actions as UserActions } from 'store/user';
import { actions as ReportsActions } from 'store/reports';
import { ActionButton, DisplayModal } from 'components';
import { reportIdentifierData } from 'constants.js';

const useStyles = makeStyles(theme => ({
  root: {},
  title: {
    paddingBottom: theme.spacing(2)
  },
  content: {
    padding: 0
  },
  closeButton: {
    position: 'absolute',
    right: theme.spacing(1),
    top: theme.spacing(1),
    color: theme.palette.warning.main
  },
  actions: {
    justifyContent: 'flex-end'
  },
  rightCol: {
    textAlign: 'right'
  },
  tableRow: {
    cursor: 'pointer'
  },
  statsContainer: {
    marginBottom: theme.spacing(2)
  },
  statsColumn: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2)
  },
  clickMetricsTableCard: {
    marginBottom: theme.spacing(2)
  },
  clickMetricsTableTitle: {
    paddingBottom: theme.spacing(1)
  },
  clickMetricsTableContent: {
    paddingBottom: theme.spacing(1)
  },
  clickMetricsTableScrollArea: {
    maxHeight: 240,
    overflowY: 'auto'
  },
  clickMetricsUrlCell: {
    maxWidth: 0,
    width: '100%'
  },
  clickMetricsUrlText: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    display: 'block'
  },
  clickMetricsCountCell: {
    whiteSpace: 'nowrap',
    width: 160
  },
  statCard: {
    height: 'auto'
  },
  stat: {
    minWidth: 100
  },
  statTitle: {
    fontWeight: 700
  },
  avatar: {
    height: 56,
    width: 56
  },
  icon: {
    height: 32,
    width: 32
  }
}));

const ActivityTable = props => {
  const { report, providerMetric } = props;
  const classes = useStyles();

  const dispatch = useDispatch();
  const { campaignData, campaignDataCount, queryData, updateParams } = report;
  const rowsPerPage = useSelector(
    store => store.user.selectedRowsPerPage || 10
  );
  const campaigns = useSelector(store => store.campaign.list || []);
  const clickedUrls = useSelector(store => store.reports.clickedUrls.data || []);
  const isClickedUrlsLoading = useSelector(store => store.reports.clickedUrls.getting);
  const emailClickMetrics = useSelector(store => store.reports.emailClickMetrics);

  const { setRowsPerPage } = UserActions;
  const { getClickedUrls, getEmailClickMetrics } = ReportsActions;
  
  const [page, setPage] = useState(queryData.index || 0);
  const [openModal, setOpenModal] = useState(false);
  const [selectedEventData, setSelectedEventData] = useState({});
  const [uniqueFieldData, setUniqueFieldData] = useState(null);
  const [openClickedUrlsModal, setOpenClickedUrlsModal] = useState(false);
  const [copiedUrlIndex, setCopiedUrlIndex] = useState(null);
  const copyTooltipTimerRef = useRef(null);
  const lastEmailClickMetricsKeyRef = useRef('');

  const emailClickMetricsRows = Array.isArray(emailClickMetrics?.data)
    ? emailClickMetrics.data
    : [];

  const totalClicks = emailClickMetricsRows.reduce((sum, row) => sum + (row.count || 0), 0);
  const uniqueLinks = emailClickMetricsRows.length;

  const getClickMetricUrl = row =>
    row?.url || row?.clickedUrl || row?.link || row?.clickedURL || row?.destinationUrl || row?.redirectUrl || '-';

  const getClickMetricCount = row =>
    row?.count || row?.totalClicks || row?.clickCount || 0;

  useEffect(() => {
    return () => {
      if (copyTooltipTimerRef.current) {
        clearTimeout(copyTooltipTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    let campaign = campaigns.find(
      campaign => campaign.id === queryData.campaignId || campaign.uuid === queryData.campaignId
    );
    if (campaign) {
      let fieldData = reportIdentifierData.filter(
        data =>
          data.productName === campaign.productName &&
          data.eventTypeCategory === campaign.eventTypeCategory
      );
      if (fieldData.length === 1) {
        setUniqueFieldData(fieldData[0]);
      } else {
        setUniqueFieldData(null);
      }
    }
  }, [campaigns, queryData.campaignId]);

  useEffect(() => {
    lastEmailClickMetricsKeyRef.current = '';
  }, [providerMetric]);

  useEffect(() => {
    // Wait for all required values to be set before dispatching
    if (providerMetric !== 'CLICKED') return;

    // Only dispatch when all required values are available
    if (!queryData?.campaignId || !queryData?.fromDate || !queryData?.toDate) {
      // Do nothing until all values are set; effect will re-run when they are
      return;
    }

    const fromDate = queryData.fromDate;
    const toDate = queryData.toDate;
    const requestKey = `${queryData.campaignId}-${fromDate}-${toDate}`;
    if (lastEmailClickMetricsKeyRef.current === requestKey) {
      return;
    }

    lastEmailClickMetricsKeyRef.current = requestKey;
    dispatch(
      getEmailClickMetrics({
        campaignId: queryData.campaignId,
        fromDate,
        toDate
      })
    );
  }, [providerMetric, queryData.campaignId, queryData.fromDate, queryData.toDate, queryData.dateType, dispatch]);

  /* eslint-disable react-hooks/exhaustive-deps */

  const handlePageChange = (event, pg) => {
    setPage(pg);
    updateParams('index', pg);
  };

  const handleRowsPerPageChange = event => {
    dispatch(setRowsPerPage(Number(event.target.value)));
    handlePageChange({}, 0);
  };

  const openEventDataModal = eventData => {
    setOpenModal(true);
    let eventIdentifierValues = Object.fromEntries(
      Object.entries(eventData).filter(
        data => data[0] !== uniqueFieldData?.fieldName
      )
    );
    setSelectedEventData(eventIdentifierValues);
  };

  const openClickedUrls = (campaign) => {
    const messageId = campaign?.eventIdentifierValues?.messageId;
    
    if (!messageId) {
      console.error('No messageId found for this campaign');
      return;
    }
    
    setOpenClickedUrlsModal(true);
    dispatch(getClickedUrls({ messageId }));
  };

  const copyUrlToClipboard = async (tooltipKey, urlToCopy) => {
    try {
      await navigator.clipboard.writeText(urlToCopy);
    } catch (error) {
      const textArea = document.createElement('textarea');
      textArea.value = urlToCopy;
      textArea.style.position = 'fixed';
      textArea.style.opacity = '0';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      try {
        document.execCommand('copy');
      } finally {
        document.body.removeChild(textArea);
      }
    }

    setCopiedUrlIndex(tooltipKey);
    if (copyTooltipTimerRef.current) {
      clearTimeout(copyTooltipTimerRef.current);
    }
    copyTooltipTimerRef.current = setTimeout(() => {
      setCopiedUrlIndex(null);
    }, 1500);
  };

  const getRowActions = campaign => [
    {
      name: 'View Details',
      action: ['ACTION', () => openEventDataModal(campaign.eventIdentifierValues)]
    },
    ...(providerMetric === 'CLICKED'
      ? [
          {
            name: 'View Clicked URLs',
            action: ['ACTION', () => openClickedUrls(campaign)]
          }
        ]
      : [])
  ];

  const hasUniqueData = () =>
    campaignData.filter(
      campaign => campaign.eventIdentifierValues[uniqueFieldData?.fieldName]
    ).length > 0;

  const getMetricDisplayName = (metric) => {
    const metricNames = {
      REQUESTED: 'REQUESTED',
      ACCEPTED: 'ACCEPTED',
      DELIVERED: 'DELIVERED',
      OPENED: 'OPENED',
      CLICKED: 'CLICKED',
      BOUNCED: 'BOUNCED',
      BOUNCE: 'BOUNCED',
      BLOCKED: 'BLOCKED'
    };
    return metricNames[metric] || metric;
  };

  return (
    <>
      <Box mb={2} display="flex" alignItems="center">
        <Typography variant="h6" style={{ fontWeight: 600, color: '#333' }}>
          Live Activitiy Metrics For: <span style={{ color: '#43a047', fontWeight: 700 }}>{getMetricDisplayName(providerMetric)}</span>
        </Typography>
      </Box>

      {providerMetric === 'CLICKED' && (
        emailClickMetrics?.getting ? (
          <Grid container spacing={4} className={classes.statsContainer}>
            <Grid item md={3} xs={12}>
              <div className={classes.statsColumn}>
                <Card className={classes.statCard}>
                  <CardContent>
                    <Skeleton height={16} width={110} style={{ marginBottom: 8 }} />
                    <Skeleton height={36} width={90} />
                  </CardContent>
                </Card>
                <Card className={classes.statCard}>
                  <CardContent>
                    <Skeleton height={16} width={110} style={{ marginBottom: 8 }} />
                    <Skeleton height={36} width={90} />
                  </CardContent>
                </Card>
              </div>
            </Grid>
            <Grid item md={9} xs={12}>
              <Card className={classes.clickMetricsTableCard}>
                <CardContent className={classes.clickMetricsTableContent}>
                  <Skeleton height={20} width={180} style={{ marginBottom: 16 }} />
                  <Skeleton height={44} style={{ marginBottom: 8 }} />
                  <Skeleton height={44} style={{ marginBottom: 8 }} />
                  <Skeleton height={44} style={{ marginBottom: 8 }} />
                  <Skeleton height={44} style={{ marginBottom: 8 }} />
                  <Skeleton height={44} />
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        ) : emailClickMetrics?.error ? (
          <Typography color="error">Unable to load email click metrics.</Typography>
        ) : (
          <Grid container spacing={4} className={classes.statsContainer}>
            <Grid item md={3} xs={12}>
              <div className={classes.statsColumn}>
                <Card className={classes.statCard}>
                  <CardContent>
                    <Grid container justify="space-between">
                      <Grid item className={classes.stat}>
                        <Typography className={classes.statTitle} color="textSecondary" gutterBottom variant="body2">
                          TOTAL CLICKS
                        </Typography>
                        <Typography variant="h3">{new Intl.NumberFormat().format(totalClicks)}</Typography>
                      </Grid>
                      <Grid item>
                        <Avatar className={classes.avatar} style={{ backgroundColor: '#443742' }}>
                          <MouseIcon className={classes.icon} />
                        </Avatar>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>

                <Card className={classes.statCard}>
                  <CardContent>
                    <Grid container justify="space-between">
                      <Grid item className={classes.stat}>
                        <Typography className={classes.statTitle} color="textSecondary" gutterBottom variant="body2">
                          UNIQUE LINKS
                        </Typography>
                        <Typography variant="h3">{new Intl.NumberFormat().format(uniqueLinks)}</Typography>
                      </Grid>
                      <Grid item>
                        <Avatar className={classes.avatar} style={{ backgroundColor: '#496DDB' }}>
                          <LinkIcon className={classes.icon} />
                        </Avatar>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </div>
            </Grid>

            <Grid item md={9} xs={12}>
              <Card className={classes.clickMetricsTableCard}>
                <CardContent className={classes.clickMetricsTableContent}>
                  <Typography variant="h6" className={classes.clickMetricsTableTitle}>
                    Clicked URLs
                  </Typography>
                  <div className={classes.clickMetricsTableScrollArea}>
                    <Table stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell>Link URL</TableCell>
                          <TableCell className={classes.clickMetricsCountCell}>Total Clicks</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {emailClickMetricsRows.length > 0 ? (
                          emailClickMetricsRows.map((row, index) => {
                            const url = getClickMetricUrl(row);
                            const count = getClickMetricCount(row);

                            return (
                              <TableRow hover key={`${url}-${index}`}>
                                <TableCell className={classes.clickMetricsUrlCell}>
                                  <Tooltip title={url}>
                                    <Typography variant="body2" className={classes.clickMetricsUrlText}>
                                      {url}
                                    </Typography>
                                  </Tooltip>
                                </TableCell>
                                <TableCell className={classes.clickMetricsCountCell}>
                                  {new Intl.NumberFormat().format(count)}
                                </TableCell>
                              </TableRow>
                            );
                          })
                        ) : (
                          <TableRow>
                            <TableCell colSpan={2}>
                              <Typography variant="body2" color="textSecondary">
                                No click metrics found for the selected date range.
                              </Typography>
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                    </div>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )
      )}

      <Card className={clsx(classes.root, classes.title)}>
        <CardContent className={classes.content}>
          <PerfectScrollbar>
            <div className={classes.inner}>
              <Table>
                <TableHead>
                  <TableRow>
                    {uniqueFieldData && hasUniqueData() && (
                      <TableCell>{uniqueFieldData.columnName}</TableCell>
                    )}
                    <TableCell>To</TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {campaignData.map((campaign, i) => (
                    <TableRow className={classes.tableRow} hover key={i}>
                      {uniqueFieldData && hasUniqueData() && (
                        <TableCell>
                          {campaign.eventIdentifierValues[
                            uniqueFieldData.fieldName
                          ] || '-'}
                        </TableCell>
                      )}
                      <TableCell>{campaign.toAddress}</TableCell>
                      <TableCell>{campaign.requestDate}</TableCell>
                      <TableCell className={classes.rightCol}>
                        <ActionButton options={getRowActions(campaign)} />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </PerfectScrollbar>
        </CardContent>
        <CardActions className={classes.actions}>
          <TablePagination
            component="div"
            count={campaignDataCount}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleRowsPerPageChange}
            page={page}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={[5, 10, 25]}
          />
        </CardActions>
      </Card>

      {openModal && (
        <DisplayModal
          batch={selectedEventData}
          title={'Event Data'}
          isOpen={openModal}
          setIsOpen={setOpenModal}
          isLoading={false}
          emptyMessage={{
            show: Object.entries(selectedEventData).length === 0,
            msg: 'No Event Data.'
          }}
          size="md"
          isConversionRequired={true}
        />
      )}

      {openClickedUrlsModal && (
        <Dialog
          fullWidth
          maxWidth="sm"
          open={openClickedUrlsModal}
          onClose={() => setOpenClickedUrlsModal(false)}
          aria-labelledby="clicked-urls-dialog-title">
          <DialogTitle id="clicked-urls-dialog-title" style={{ position: 'relative', paddingRight: '48px' }}>
            <Typography variant="h2">Clicked URLs</Typography>
            <IconButton
              aria-label="close"
              className={classes.closeButton}
              onClick={() => setOpenClickedUrlsModal(false)}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers style={{ maxHeight: '350px', overflowY: 'auto', padding: 0 }}>
            {isClickedUrlsLoading ? (
              <div style={{ display: 'flex', justifyContent: 'center', padding: '24px' }}>
                <CircularProgress />
              </div>
            ) : clickedUrls.length === 0 ? (
              <Typography variant="body1" style={{ padding: '16px' }}>No Clicked URLs.</Typography>
            ) : (
              <List disablePadding>
                {clickedUrls.map((url, index) => (
                  <ListItem 
                    key={`${url}-${index}`} 
                    style={{ 
                      padding: '12px 16px',
                      display: 'flex',
                      alignItems: 'center',
                      borderBottom: index < clickedUrls.length - 1 ? '1px solid #e0e0e0' : 'none'
                    }}
                  >
                    <Tooltip 
                      title={url} 
                      placement="right-end"
                      PopperProps={{
                        modifiers: {
                          flip: {
                            enabled: false
                          }
                        }
                      }}
                    >
                      <Typography
                        variant="body1"
                        style={{
                          display: 'block',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          flex: 1,
                          minWidth: 0,
                          width: '28rem',
                          maxWidth: '100%',
                          paddingRight: '12px'
                        }}
                      >
                        {url}
                      </Typography>
                    </Tooltip>
                    <Tooltip
                      title={copiedUrlIndex === `${url}-${index}` ? 'Copied to Clipboard' : 'Copy link'}
                      placement="bottom-end"
                      open={copiedUrlIndex === `${url}-${index}` ? true : undefined}
                    >
                      <IconButton
                        aria-label="copy-link"
                        size="small"
                        onClick={() => copyUrlToClipboard(`${url}-${index}`, url)}>
                        <FileCopyOutlinedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </ListItem>
                ))}
              </List>
            )}
          </DialogContent>
          <DialogActions>
            <Button
              variant="contained"
              color="primary"
              onClick={() => setOpenClickedUrlsModal(false)}>
              Close
            </Button>
          </DialogActions>
        </Dialog>
      )}

    </>
  );
};

ActivityTable.propTypes = {
  className: PropTypes.string,
  selectionChanged: PropTypes.func,
  search: PropTypes.string
};

export default ActivityTable;
